import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  SystemSkill,
  SystemSkillDatabase,
  SkillPropertySchema,
  SkillDatabaseExport
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface SystemSkillStore {
  databases: SystemSkillDatabase[]

  // データベース操作
  addDatabase: (database: Omit<SystemSkillDatabase, 'id' | 'createdAt' | 'updatedAt'>) => string
  updateDatabase: (id: string, updates: Partial<SystemSkillDatabase>) => void
  removeDatabase: (id: string) => void
  getDatabase: (id: string) => SystemSkillDatabase | undefined
  getDatabaseByName: (systemName: string) => SystemSkillDatabase | undefined

  // スキル操作
  addSkill: (databaseId: string, skill: Omit<SystemSkill, 'id' | 'systemId' | 'createdAt' | 'updatedAt'>) => void
  addBatchSkills: (databaseId: string, skills: Omit<SystemSkill, 'id' | 'systemId' | 'createdAt' | 'updatedAt'>[]) => void
  updateSkill: (databaseId: string, skillId: string, updates: Partial<SystemSkill>) => void
  removeSkill: (databaseId: string, skillId: string) => void
  getSkill: (databaseId: string, skillId: string) => SystemSkill | undefined

  // 検索・フィルター
  searchSkills: (databaseId: string, query: string) => SystemSkill[]
  getSkillsByTag: (databaseId: string, tag: string) => SystemSkill[]
  getAllSkillsFlat: () => { skill: SystemSkill; database: SystemSkillDatabase }[]

  // インポート/エクスポート
  importFromText: (databaseId: string, text: string, separator?: string) => number
  importFromJSON: (jsonString: string) => void
  exportAsJSON: () => string
  exportDatabaseAsJSON: (databaseId: string) => string

  // ユーティリティ
  clearAll: () => void
}

const MAX_DATABASES = 50
const MAX_SKILLS_PER_DATABASE = 1000

export const useSystemSkillStore = create<SystemSkillStore>()(
  persist(
    (set, get) => ({
      databases: [],

      // データベース操作
      addDatabase: (database) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        const newDatabase: SystemSkillDatabase = {
          ...database,
          id,
          skills: database.skills || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => {
          let databases = [newDatabase, ...state.databases]
          if (databases.length > MAX_DATABASES) {
            databases = databases.slice(0, MAX_DATABASES)
          }
          return { databases }
        })

        return id
      },

      updateDatabase: (id, updates) => {
        set(state => ({
          databases: state.databases.map(db =>
            db.id === id
              ? { ...db, ...updates, updatedAt: new Date().toISOString() }
              : db
          )
        }))
      },

      removeDatabase: (id) => {
        set(state => ({
          databases: state.databases.filter(db => db.id !== id)
        }))
      },

      getDatabase: (id) => {
        return get().databases.find(db => db.id === id)
      },

      getDatabaseByName: (systemName) => {
        return get().databases.find(db =>
          db.systemName.toLowerCase() === systemName.toLowerCase()
        )
      },

      // スキル操作
      addSkill: (databaseId, skill) => {
        const skillId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        const newSkill: SystemSkill = {
          ...skill,
          id: skillId,
          systemId: databaseId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => ({
          databases: state.databases.map(db => {
            if (db.id !== databaseId) return db
            let skills = [newSkill, ...db.skills]
            if (skills.length > MAX_SKILLS_PER_DATABASE) {
              skills = skills.slice(0, MAX_SKILLS_PER_DATABASE)
            }
            return { ...db, skills, updatedAt: new Date().toISOString() }
          })
        }))
      },

      addBatchSkills: (databaseId, batchSkills) => {
        const now = Date.now()
        const newSkills: SystemSkill[] = batchSkills.map((skill, index) => ({
          ...skill,
          id: `${now + index}_${Math.random().toString(36).slice(2, 11)}`,
          systemId: databaseId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

        set(state => ({
          databases: state.databases.map(db => {
            if (db.id !== databaseId) return db
            let skills = [...newSkills, ...db.skills]
            if (skills.length > MAX_SKILLS_PER_DATABASE) {
              skills = skills.slice(0, MAX_SKILLS_PER_DATABASE)
            }
            return { ...db, skills, updatedAt: new Date().toISOString() }
          })
        }))
      },

      updateSkill: (databaseId, skillId, updates) => {
        set(state => ({
          databases: state.databases.map(db => {
            if (db.id !== databaseId) return db
            return {
              ...db,
              skills: db.skills.map(skill =>
                skill.id === skillId
                  ? { ...skill, ...updates, updatedAt: new Date().toISOString() }
                  : skill
              ),
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },

      removeSkill: (databaseId, skillId) => {
        set(state => ({
          databases: state.databases.map(db => {
            if (db.id !== databaseId) return db
            return {
              ...db,
              skills: db.skills.filter(skill => skill.id !== skillId),
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },

      getSkill: (databaseId, skillId) => {
        const db = get().databases.find(db => db.id === databaseId)
        return db?.skills.find(skill => skill.id === skillId)
      },

      // 検索・フィルター
      searchSkills: (databaseId, query) => {
        const db = get().databases.find(db => db.id === databaseId)
        if (!db) return []

        const lowerQuery = query.toLowerCase()
        return db.skills.filter(skill =>
          skill.name.toLowerCase().includes(lowerQuery) ||
          skill.description?.toLowerCase().includes(lowerQuery) ||
          skill.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
        )
      },

      getSkillsByTag: (databaseId, tag) => {
        const db = get().databases.find(db => db.id === databaseId)
        if (!db) return []

        return db.skills.filter(skill =>
          skill.tags?.includes(tag)
        )
      },

      getAllSkillsFlat: () => {
        const result: { skill: SystemSkill; database: SystemSkillDatabase }[] = []
        for (const db of get().databases) {
          for (const skill of db.skills) {
            result.push({ skill, database: db })
          }
        }
        return result
      },

      // インポート
      importFromText: (databaseId, text, separator = '\n') => {
        const lines = text.split(separator).map(line => line.trim()).filter(Boolean)
        const skills: Omit<SystemSkill, 'id' | 'systemId' | 'createdAt' | 'updatedAt'>[] = []

        for (const line of lines) {
          // 形式: "スキル名" または "スキル名: 説明" または "スキル名 (カテゴリ): 説明"
          const match = line.match(/^(.+?)(?:\s*\((.+?)\))?(?:\s*[:：]\s*(.+))?$/)
          if (match) {
            const [, name, category, description] = match
            skills.push({
              name: name.trim(),
              properties: category ? { category: category.trim() } : undefined,
              description: description?.trim()
            })
          }
        }

        if (skills.length > 0) {
          get().addBatchSkills(databaseId, skills)
        }

        return skills.length
      },

      importFromJSON: (jsonString) => {
        try {
          const data = JSON.parse(jsonString) as SkillDatabaseExport | SystemSkillDatabase[]

          // エクスポート形式の場合
          if ('databases' in data) {
            for (const db of data.databases) {
              get().addDatabase({
                systemName: db.systemName,
                version: db.version,
                description: db.description,
                skills: db.skills,
                propertySchema: db.propertySchema,
                tags: db.tags
              })
            }
          }
          // 配列形式の場合
          else if (Array.isArray(data)) {
            for (const db of data) {
              get().addDatabase({
                systemName: db.systemName,
                version: db.version,
                description: db.description,
                skills: db.skills,
                propertySchema: db.propertySchema,
                tags: db.tags
              })
            }
          }
        } catch (error) {
          console.error('Failed to import JSON:', error)
          throw new Error('JSONの解析に失敗しました')
        }
      },

      // エクスポート
      exportAsJSON: () => {
        const exportData: SkillDatabaseExport = {
          version: '1.0',
          exportedAt: new Date().toISOString(),
          databases: get().databases
        }
        return JSON.stringify(exportData, null, 2)
      },

      exportDatabaseAsJSON: (databaseId) => {
        const db = get().databases.find(db => db.id === databaseId)
        if (!db) return '{}'
        return JSON.stringify(db, null, 2)
      },

      // ユーティリティ
      clearAll: () => {
        set({ databases: [] })
      }
    }),
    {
      name: 'system-skill-store',
      storage: createIndexedDBStorage('system-skill-store'),
      version: 1
    }
  )
)
